"use client";
import React from 'react';
import { motion } from 'framer-motion';
import { useContactModal } from '../../../../contexts/ContactModalContext';

/* ─── Step SVG icons ─── */
const IconDiscover = () => (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
        <circle cx="16" cy="16" r="8" stroke="#080400" strokeWidth="2.8" strokeLinecap="round"/>
        <path d="M22 22L30 30" stroke="#080400" strokeWidth="2.8" strokeLinecap="round"/>
        <path d="M13 16h6M16 13v6" stroke="#080400" strokeWidth="2.5" strokeLinecap="round"/>
    </svg>
);
const IconStrategize = () => (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
        <path d="M18 4l2.5 8h8.5l-6.9 5 2.6 8L18 21l-6.6 4 2.6-8L7 12h8.5L18 4z" stroke="#080400" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round"/>
    </svg>
);
const IconExecute = () => (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
        <path d="M10 26S8 14 18 6c10-8 20 2 20 2s0 10-8 14c-4 2-8 1-8 1" stroke="#080400" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M10 26l-4 4" stroke="#080400" strokeWidth="2.5" strokeLinecap="round"/>
        <circle cx="21" cy="15" r="2.5" fill="#080400"/>
    </svg>
);
const IconOptimize = () => (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
        <path d="M6 10l4-4 4 4M10 6v16" stroke="#080400" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M30 26l-4 4-4-4M26 30V14" stroke="#080400" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M10 22c0 4 3 6 6 6" stroke="#080400" strokeWidth="2.5" strokeLinecap="round"/>
        <path d="M26 14c0-4-3-6-6-6" stroke="#080400" strokeWidth="2.5" strokeLinecap="round"/>
    </svg>
);
const IconScale = () => (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
        <path d="M6 28L13 18l7 5L28 8" stroke="#080400" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M22 8h6v6" stroke="#080400" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="28" cy="28" r="4" stroke="#080400" strokeWidth="2.2"/>
        <path d="M26.5 28l1.5 1.5L30 27" stroke="#080400" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const steps = [
    {
        title: 'Discover',
        desc: 'Before we build campaigns, we immerse ourselves in your business. We study your audience, motivations, objections, buying triggers, competitors, and customer journey. Because successful performance marketing starts with understanding, not assumptions.',
        Icon: IconDiscover,
    },
    {
        title: 'Strategize',
        desc: 'Using those insights, we create a growth strategy tailored to your market, your goals, and the way your customers make decisions. Not a template. Not a recycled playbook. A strategy built around your business.',
        Icon: IconStrategize,
    },
    {
        title: 'Execute',
        desc: 'Campaigns, creative assets, landing pages, Google Ads, Meta Ads, tracking systems, and analytics are launched as one connected growth engine.',
        Icon: IconExecute,
    },
    {
        title: 'Optimize',
        desc: 'Consumer behaviour leaves signals. We analyse those signals continuously, testing messaging, audiences, creatives, landing pages, and offers to improve performance over time.',
        Icon: IconOptimize,
    },
    {
        title: 'Scale',
        desc: 'Once the system consistently produces profitable results, we scale intelligently while protecting efficiency, lead quality, and acquisition costs.',
        Icon: IconScale,
    },
];

/* Down arrow for mobile */
const ArrowDown = ({ delay = 0 }: { delay?: number }) => (
    <div className="flex lg:hidden w-full items-center justify-center py-1">
        <motion.svg
            width="16" height="32" viewBox="0 0 16 32" fill="none"
            animate={{ y: [0, 6, 0] }}
            transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut', delay }}
        >
            <path d="M8 2v20" stroke="#ceff1a" strokeWidth="1.8" strokeLinecap="round"/>
            <path d="M3 18l5 8 5-8" stroke="#ceff1a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        </motion.svg>
    </div>
);

export default function PerformanceApproach() {
    const { openModal } = useContactModal();
    return (
        <section
            className="relative overflow-hidden py-24 px-6 md:px-16 lg:px-24"
            style={{ background: '#14101a' }}
        >
            {/* Subtle texture */}
            <div
                className="absolute inset-0 pointer-events-none opacity-[0.025]"
                style={{
                    backgroundImage: 'radial-gradient(circle, rgba(206,255,26,1) 1px, transparent 1px)',
                    backgroundSize: '40px 40px',
                }}
            />

            <div className="max-w-[1400px] mx-auto flex flex-col gap-20 relative z-10">

                {/* Header */}
                <div className="flex flex-col gap-4 max-w-2xl">
                    <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="text-xs font-bold tracking-[0.25em] uppercase"
                        style={{ color: '#ceff1a', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}
                    >
                        Our Process
                    </motion.span>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="font-bold tracking-tight leading-tight"
                        style={{
                            fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                            fontSize: 'clamp(36px, 4vw, 64px)',
                            color: '#fbfdfd',
                        }}
                    >
                        How We Build Your Growth Engine
                    </motion.h2>
                </div>

                {/* Flow row — desktop horizontal, mobile vertical */}
                <div className="flex flex-col lg:flex-row items-start lg:items-start gap-0">
                    {steps.map((step, i) => (
                        <React.Fragment key={i}>
                            {/* Step card */}
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.55, delay: i * 0.12 }}
                                className="flex flex-col items-center lg:items-start gap-5 flex-1 min-w-0 w-full lg:w-auto"
                            >
                                {/* Circle icon */}
                                <div
                                    className="flex items-center justify-center rounded-full shrink-0"
                                    style={{
                                        width: 88,
                                        height: 88,
                                        background: '#ceff1a',
                                    }}
                                >
                                    <step.Icon />
                                </div>

                                {/* Text */}
                                <div className="flex flex-col items-center lg:items-start gap-2 text-center lg:text-left">
                                    <h3
                                        className="font-bold leading-snug"
                                        style={{
                                            fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                                            fontSize: 'clamp(18px, 1.5vw, 22px)',
                                            color: '#fbfdfd',
                                            letterSpacing: '0.02em',
                                        }}
                                    >
                                        {step.title}
                                    </h3>
                                    <p
                                        className="text-sm leading-relaxed"
                                        style={{
                                            fontFamily: 'var(--font-manrope), Manrope, sans-serif',
                                            color: 'rgba(251,253,253,0.65)',
                                            maxWidth: 260,
                                        }}
                                    >
                                        {step.desc}
                                    </p>
                                </div>
                            </motion.div>

                            {/* Connector arrow (not after last step) */}
                            {i < steps.length - 1 && (
                                <>
                                    {/* Desktop: horizontal arrow aligned with circle centre */}
                                    <div
                                        className="hidden lg:flex items-start justify-center shrink-0 pt-[36px]"
                                        style={{ width: 56 }}
                                    >
                                        <motion.div
                                            initial={{ scaleX: 0, opacity: 0 }}
                                            whileInView={{ scaleX: 1, opacity: 1 }}
                                            viewport={{ once: true }}
                                            transition={{ duration: 0.4, delay: i * 0.12 + 0.3 }}
                                            style={{ transformOrigin: 'left' }}
                                        >
                                            <motion.svg
                                                width="56" height="16" viewBox="0 0 56 16" fill="none"
                                                animate={{ x: [0, 6, 0] }}
                                                transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut', delay: i * 0.12 + 0.7 }}
                                            >
                                                <path d="M2 8h40" stroke="#ceff1a" strokeWidth="1.8" strokeLinecap="round"/>
                                                <path d="M38 3l8 5-8 5" stroke="#ceff1a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                            </motion.svg>
                                        </motion.div>
                                    </div>
                                    {/* Mobile: vertical arrow */}
                                    <ArrowDown delay={i * 0.12 + 0.7} />
                                </>
                            )}
                        </React.Fragment>
                    ))}
                </div>

                {/* CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="text-center"
                >
                    <button
                        onClick={openModal}
                        className="inline-flex items-center justify-center px-10 py-5 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105"
                        style={{
                            background: '#ceff1a',
                            color: '#080400',
                            fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                            letterSpacing: '0.08em',
                        }}
                    >
                        Start With a Free Audit →
                    </button>
                </motion.div>

            </div>
        </section>
    );
}
